import { useCallback, useEffect, useRef, useState } from 'react'
import {
    CommonConstant,
    ErrorConstant,
    TaskDashboardService,
} from 'utility'
import { CUSTOM_CLIENTS_COMPANY_ID } from 'hooks/customer'
import { buildCustomerDisplayText } from 'page-components/group/notification-group-utils'
import {
    buildStatusQuery,
    DASHBOARD_PREVIEW_ANSWER_COUNT,
    DASHBOARD_STATUS_FILTER,
    filterOverdueTasks,
    getDefaultDateRange,
    normalizeDashboardResponse,
    normalizeDashboardTasks,
} from 'page-components/task/dashboard/task-dashboard-utils'

const toArray = value => (Array.isArray(value) ? value : [])

const buildCustomerDisplayTextQuery = (customerId, companyId, customersData) => {
    if (!customerId || Number(companyId) !== CUSTOM_CLIENTS_COMPANY_ID) {
        return undefined
    }

    const displayText = buildCustomerDisplayText(
        [Number(customerId)],
        toArray(customersData),
    )

    return displayText || undefined
}

const buildUiFilter = () => {
    const { startDate, endDate } = getDefaultDateRange()
    return {
        search: '',
        status: DASHBOARD_STATUS_FILTER.all,
        auditorId: '',
        templateId: '',
        customerId: '',
        taskType: '',
        startDate,
        endDate,
        pageNumber: 1,
        pageSize: CommonConstant.defaultPageSize,
        previewAnswerCount: DASHBOARD_PREVIEW_ANSWER_COUNT,
    }
}

/** Tasks API: dates omitted until user clicks Apply (Postman #3 works without dates). */
const buildAppliedTasksFilter = (source = {}) => ({
    search: source.search || '',
    status: source.status || DASHBOARD_STATUS_FILTER.all,
    auditorId: source.auditorId || '',
    templateId: source.templateId || '',
    customerId: source.customerId || '',
    taskType: source.taskType || '',
    pageNumber: source.pageNumber || 1,
    pageSize: source.pageSize || CommonConstant.defaultPageSize,
    previewAnswerCount:
        source.previewAnswerCount ?? DASHBOARD_PREVIEW_ANSWER_COUNT,
    startDate: source.startDate || '',
    endDate: source.endDate || '',
})

export const useTaskDashboard = (
    statsScope = 'auto',
    { companyId, customersData } = {},
) => {
    const [stats, setStats] = useState(null)
    const [scope, setScope] = useState(statsScope)
    const [statsLoading, setStatsLoading] = useState(false)
    const [tasksLoading, setTasksLoading] = useState(false)
    const [error, setError] = useState('')
    const [apiTasks, setApiTasks] = useState([])
    const [apiTotalCount, setApiTotalCount] = useState(0)
    const [taskFacets, setTaskFacets] = useState(null)
    const [quickStatusFilter, setQuickStatusFilter] = useState(
        DASHBOARD_STATUS_FILTER.all,
    )
    const [quickAuditorFilter, setQuickAuditorFilter] = useState('')
    const [quickCustomerFilter, setQuickCustomerFilter] = useState('')
    const [filter, setFilter] = useState(buildUiFilter)
    const [appliedFilter, setAppliedFilter] = useState(() =>
        buildAppliedTasksFilter(buildUiFilter()),
    )
    const statsFetchIdRef = useRef(0)
    const tasksFetchIdRef = useRef(0)

    useEffect(() => {
        setScope(statsScope)
    }, [statsScope])

    const fetchStats = useCallback(async (activeFilter, activeScope) => {
        statsFetchIdRef.current += 1
        const fetchId = statsFetchIdRef.current
        try {
            setStatsLoading(true)
            const statsRange = getDefaultDateRange()
            const result = await TaskDashboardService.getStats({
                startDate: activeFilter.startDate || statsRange.startDate,
                endDate: activeFilter.endDate || statsRange.endDate,
                scope: activeScope || 'auto',
            })
            if (fetchId !== statsFetchIdRef.current) return
            setStats(normalizeDashboardResponse(result))
            setError('')
        } catch {
            if (fetchId !== statsFetchIdRef.current) return
            setError(ErrorConstant.default)
        } finally {
            if (fetchId === statsFetchIdRef.current) {
                setStatsLoading(false)
            }
        }
    }, [])

    const fetchTasks = useCallback(async (activeFilter) => {
        tasksFetchIdRef.current += 1
        const fetchId = tasksFetchIdRef.current
        try {
            setTasksLoading(true)
            const statusQuery = buildStatusQuery(activeFilter.status)
            const usesCustomClients =
                Number(companyId) === CUSTOM_CLIENTS_COMPANY_ID
            const customerDisplayText = buildCustomerDisplayTextQuery(
                activeFilter.customerId,
                companyId,
                customersData,
            )
            const query = {
                search: activeFilter.search || undefined,
                status: statusQuery || undefined,
                auditorId: activeFilter.auditorId || undefined,
                templateId: activeFilter.templateId || undefined,
                customerId:
                    usesCustomClients && customerDisplayText
                        ? undefined
                        : activeFilter.customerId || undefined,
                CustomerDisplayText: customerDisplayText,
                taskType: activeFilter.taskType || undefined,
                startDate: activeFilter.startDate ? activeFilter.startDate : undefined,
                endDate: activeFilter.endDate ? activeFilter.endDate : undefined,
                pageNumber: activeFilter.pageNumber,
                pageSize: activeFilter.pageSize,
                previewAnswerCount: activeFilter.previewAnswerCount,
            }
            const result = await TaskDashboardService.getTasks(query)
            if (fetchId !== tasksFetchIdRef.current) return
            const normalized = normalizeDashboardResponse(result)
            let items = normalizeDashboardTasks(normalized.items || [])
            if (activeFilter.status === 'overdue') {
                items = filterOverdueTasks(items)
            }
            setApiTasks(items)
            setApiTotalCount(
                activeFilter.status === 'overdue'
                    ? items.length
                    : normalized.totalCount || 0,
            )
            setTaskFacets(normalized.facets ?? null)
            setError('')
        } catch {
            if (fetchId !== tasksFetchIdRef.current) return
            setError(ErrorConstant.default)
        } finally {
            if (fetchId === tasksFetchIdRef.current) {
                setTasksLoading(false)
            }
        }
    }, [companyId, customersData])

    const tasks = apiTasks
    const totalCount = apiTotalCount

    useEffect(() => {
        fetchStats(appliedFilter, scope)
        fetchTasks(appliedFilter)
    }, [appliedFilter, scope, fetchStats, fetchTasks])

    const applyFilters = () => {
        const nextStatus = filter.status || DASHBOARD_STATUS_FILTER.all
        setQuickStatusFilter(nextStatus)
        setQuickAuditorFilter(filter.auditorId || '')
        setQuickCustomerFilter(filter.customerId || '')
        setAppliedFilter(
            buildAppliedTasksFilter({
                ...filter,
                pageNumber: 1,
            }),
        )
    }

    const applyFilterChanges = useCallback(changes => {
        setFilter(prev => {
            const next = {
                ...prev,
                ...changes,
                pageNumber: changes.pageNumber ?? 1,
            }
            if (changes.status !== undefined) {
                setQuickStatusFilter(changes.status)
            }
            if (changes.auditorId !== undefined) {
                setQuickAuditorFilter(changes.auditorId || '')
            }
            if (changes.customerId !== undefined) {
                setQuickCustomerFilter(changes.customerId || '')
            }
            setAppliedFilter(buildAppliedTasksFilter(next))
            return next
        })
    }, [])

    const applyQuickStatusFilter = useCallback(
        statusValue => {
            applyFilterChanges({
                status: statusValue || DASHBOARD_STATUS_FILTER.all,
                pageNumber: 1,
            })
        },
        [applyFilterChanges],
    )

    const applyQuickAuditorFilter = useCallback(
        auditorId => {
            applyFilterChanges({
                auditorId: auditorId || '',
                pageNumber: 1,
            })
        },
        [applyFilterChanges],
    )

    const applyQuickCustomerFilter = useCallback(
        customerId => {
            applyFilterChanges({
                customerId: customerId || '',
                pageNumber: 1,
            })
        },
        [applyFilterChanges],
    )

    const resetFilters = () => {
        const defaults = buildUiFilter()
        setFilter(defaults)
        setQuickStatusFilter(DASHBOARD_STATUS_FILTER.all)
        setQuickAuditorFilter('')
        setQuickCustomerFilter('')
        setAppliedFilter(buildAppliedTasksFilter(defaults))
    }

    const updateFilter = changes => {
        setFilter(prev => ({ ...prev, ...changes }))
    }

    const pageChanged = page => {
        setAppliedFilter(prev => ({ ...prev, pageNumber: page }))
        setFilter(prev => ({ ...prev, pageNumber: page }))
    }

    const refresh = useCallback(() => {
        fetchStats(appliedFilter, scope)
        fetchTasks(appliedFilter)
    }, [appliedFilter, scope, fetchStats, fetchTasks])

    return {
        stats,
        tasks,
        totalCount,
        statsLoading,
        tasksLoading,
        loading: tasksLoading,
        filtersLoading: statsLoading || tasksLoading,
        error,
        filter,
        appliedFilter,
        quickStatusFilter,
        quickAuditorFilter,
        quickCustomerFilter,
        taskFacets,
        apiTotalCount,
        scope,
        setScope,
        updateFilter,
        applyFilters,
        applyFilterChanges,
        applyQuickStatusFilter,
        applyQuickAuditorFilter,
        applyQuickCustomerFilter,
        resetFilters,
        pageChanged,
        refresh,
    }
}

/** In-memory /FullPreview cache for the current list page (invalidated on page change). */
export const useTaskFullPreviewCache = pageNumber => {
    const cacheRef = useRef({})
    const [cacheVersion, setCacheVersion] = useState(0)
    const [loadingTaskId, setLoadingTaskId] = useState(null)
    const [errorTaskId, setErrorTaskId] = useState(null)

    useEffect(() => {
        cacheRef.current = {}
        setCacheVersion(v => v + 1)
        setLoadingTaskId(null)
        setErrorTaskId(null)
    }, [pageNumber])

    const getCachedPreview = useCallback(
        taskId => {
            if (!taskId) return null
            return cacheRef.current[taskId] || null
        },
        [cacheVersion],
    )

    const loadFullPreview = useCallback(async taskId => {
        if (!taskId) return null
        if (cacheRef.current[taskId]) {
            return cacheRef.current[taskId]
        }

        setLoadingTaskId(taskId)
        setErrorTaskId(null)
        try {
            const result = await TaskDashboardService.getFullPreview(taskId)
            cacheRef.current[taskId] = result
            setCacheVersion(v => v + 1)
            return result
        } catch {
            setErrorTaskId(taskId)
            return null
        } finally {
            setLoadingTaskId(prev => (prev === taskId ? null : prev))
        }
    }, [])

    return {
        getCachedPreview,
        loadFullPreview,
        loadingTaskId,
        errorTaskId,
    }
}
